import {fromJson, type JsonValue} from '@github-ui/dotcom-schema/protobuf'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {RouteObject, ShouldRevalidateFunction} from '@github-ui/react-router'
import {sendCustomMetric} from '@github-ui/stats'

import type {AppRegistration} from '../app-routing-types'
import type {EmbeddedData} from '../embedded-data-types'
import type {
  QueryDepsFn,
  QueryRouteQueryConfig,
  QueryRouteQueryConfigGenerator,
  QueryRouteQueryType,
} from './data-router-types'
import {queryFnFetch} from './query-fn-fetch'
import {QueryRoute} from './query-route'

export type GetRoutesFunction = (args: {isEnabled: (featureName: string) => boolean | undefined}) => RouteObject[]

export interface DataRouterApplicationOptions {
  tanStackRouterEnabled?: boolean | (() => boolean)
}

interface DataRouterAppRegistration extends AppRegistration {
  App?: never
  routes: RouteObject[]
}

export class DataRouterApplication<T extends string> {
  readonly name: T
  readonly tanStackRouterEnabled: boolean | (() => boolean)
  #routesOrGetRoutes: RouteObject[] | GetRoutesFunction
  embeddedData?: EmbeddedData

  constructor(
    name: T,
    routesOrGetRoutes: RouteObject[] | GetRoutesFunction,
    options: DataRouterApplicationOptions = {},
  ) {
    this.name = name
    this.tanStackRouterEnabled = options.tanStackRouterEnabled ?? false
    this.#routesOrGetRoutes = routesOrGetRoutes
    this.registration = this.registration.bind(this)
  }

  registration(opts?: {embeddedData?: EmbeddedData}): DataRouterAppRegistration {
    this.embeddedData = opts?.embeddedData
    const routes = this.#getRoutes()
    return {
      routes,
    }
  }

  #getRoutes() {
    if (typeof this.#routesOrGetRoutes === 'function') {
      const isEnabled = (featureName: string) => {
        const enabledFeatures = this.embeddedData?.appPayload?.enabled_features as Record<string, boolean> | undefined
        if (!enabledFeatures || !(featureName in enabledFeatures)) {
          return undefined
        }
        return enabledFeatures[featureName]
      }
      return this.#routesOrGetRoutes({isEnabled})
    }
    return this.#routesOrGetRoutes
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyQueryConfigGenerator = QueryRouteQueryConfigGenerator<any, any, any, any, any, any, any>

/**
 * A query config constraint that provides contextual typing for `queryDeps` (via RoutePath)
 * while keeping all other type parameters permissive for broad compatibility.
 */
type QueryConfigConstraint<RoutePath extends string> =
  | {
      queryName: string
      queryDeps?: QueryDepsFn<RoutePath>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryFn?: (...args: any[]) => any
      type?: QueryRouteQueryType
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: any
    }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | ((...args: any[]) => any)

type ExtractQueryConfig<T, AppName extends string, RouteId extends string, RoutePath extends string> =
  T extends QueryRouteQueryConfigGenerator<
    AppName,
    string,
    RoutePath,
    infer QueryName extends string,
    // `| undefined` is required because `queryDeps` is optional in `QueryRouteQueryConfig`
    infer QueryDeps extends QueryDepsFn<RoutePath> | undefined,
    infer QueryRes,
    infer QueryType extends QueryRouteQueryType
  >
    ? {
        [K in QueryName]: QueryRouteQueryConfig<AppName, RouteId, RoutePath, QueryName, QueryDeps, QueryRes, QueryType>
      }
    : never

type MergeQueryConfigs<
  Queries extends readonly AnyQueryConfigGenerator[],
  AppName extends string,
  RouteId extends string,
  RoutePath extends string,
> = Queries extends readonly [infer First, ...infer Rest extends AnyQueryConfigGenerator[]]
  ? ExtractQueryConfig<First, AppName, RouteId, RoutePath> & MergeQueryConfigs<Rest, AppName, RouteId, RoutePath>
  : {}

// NOTE: if this classname changes from `DataRouterApplicationBuilder`, also update the react-app-name ESLint rule
// /packages/eslint-plugin-github-monorepo/rules/react-app-name.js
export class DataRouterApplicationBuilder<T extends string> {
  // NOTE: if this method name changes from `create`, also update the react-app-name ESLint rule
  // /packages/eslint-plugin-github-monorepo/rules/react-app-name.js
  static create<T extends string>(name: T) {
    return new DataRouterApplicationBuilder<T>(name)
  }

  readonly name: T
  #app?: DataRouterApplication<T>

  private constructor(name: T) {
    this.name = name
  }

  getEmbeddedData = () => {
    if (!this.#app) {
      throw new Error('getEmbeddedData should only be called after createDataRouterAppFromRoutes')
    }
    return this.#app.embeddedData
  }

  createDataRouterAppFromRoutes(routes: RouteObject[] | GetRoutesFunction, options?: DataRouterApplicationOptions) {
    this.#app = new DataRouterApplication(this.name, routes, options)
    return this.#app
  }

  // #region createQueryRouteConfig

  /**
   * Creates a query route config with a variadic list of queries.
   * Each query's name, deps, response type, and query type are inferred from the provided config.
   * The returned QueryRoute merges all query configs into a single keyed record type.
   */
  createQueryRouteConfig<
    RouteId extends string,
    RoutePath extends string,
    const Queries extends ReadonlyArray<QueryConfigConstraint<RoutePath>>,
  >(
    /** RouteId must be a valid JavaScript camelCase Identifier */
    id: RouteId,
    {
      path,
      index,
      queries,
      shouldRevalidate,
    }: {
      path: RoutePath
      index?: boolean
      queries?: [...Queries]
      shouldRevalidate?: ShouldRevalidateFunction
    },
  ): QueryRoute<string, RouteId, RoutePath, MergeQueryConfigs<Queries, string, RouteId, RoutePath>> {
    const resolvedQueries = (queries ?? []) as AnyQueryConfigGenerator[]
    assertCamelCase(id)

    return new QueryRoute({
      appName: this.name,
      id,
      path,
      queries: toKeyedQueries(resolvedQueries, id),
      index: index ?? false,
      getEmbeddedData: this.getEmbeddedData,
      shouldRevalidate,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as any
  }

  // #endregion createQueryRouteConfig
}

class DuplicateRouteQueryNameError extends Error {
  constructor(queryName: string) {
    super(`query names cannot be duplicated: \`${queryName}\` has already been defined for this route.`)
    this.name = 'DuplicateRouteQueryNameError'
  }
}

function toKeyedQueries(queries: AnyQueryConfigGenerator[], id: string) {
  const registeredQueryNames = new Set<string>()

  return Object.fromEntries(
    queries.map(configOrGenerator => {
      let query

      if (typeof configOrGenerator === 'function') {
        query = configOrGenerator(id)
      } else {
        query = configOrGenerator
      }

      const {queryName, ...config} = query

      if (registeredQueryNames.has(queryName)) {
        throw new DuplicateRouteQueryNameError(queryName)
      }
      registeredQueryNames.add(queryName)

      // If the query declares a Protobuf `schema`, wrap (or synthesize) `queryFn`
      // to validate/decode the JSON response with `fromJson(schema, ...)`.
      if (config.schema) {
        const schema = config.schema
        const userQueryFn = config.queryFn
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const validatingQueryFn = async (queryKey: any, opts: any) => {
          const raw = userQueryFn ? await userQueryFn(queryKey, opts) : await queryFnFetch<JsonValue>(queryKey)
          const start = performance.now()
          // Ignore unknown fields so that backend-only additions (fields not yet
          // present in the pinned schema) do not break consumers. This mirrors
          // the standard proto3 JSON forward-compatibility contract.
          //
          // When `react_enforce_schema` is enabled, schema mismatches surface
          // as thrown errors (failing the route's loader). When disabled, we
          // log the error and pass the raw payload through so a bad/changing
          // schema cannot break production consumers — useful while the
          // schema for a route is still being rolled out.
          try {
            const json = fromJson(schema, raw as JsonValue, {ignoreUnknownFields: true})

            sendCustomMetric({
              name: 'REACT_QUERY_SCHEMA_PARSE_TIME',
              value: performance.now() - start,
              tags: {
                routeId: id,
                queryName,
                status: 'success',
              },
            })

            return json
          } catch (error) {
            sendCustomMetric({
              name: 'REACT_QUERY_SCHEMA_ERROR',
              value: 1,
              tags: {
                routeId: id,
                queryName,
              },
            })

            sendCustomMetric({
              name: 'REACT_QUERY_SCHEMA_PARSE_TIME',
              value: performance.now() - start,
              tags: {
                routeId: id,
                queryName,
                status: 'error',
              },
            })

            if (isFeatureEnabled('react_enforce_schema')) {
              throw error
            } else {
              // Log the error without breaking the route
              setTimeout(() => {
                throw error
              })
            }

            return raw
          }
        }
        config.queryFn = validatingQueryFn
        delete config.schema
      } else if (!config.queryFn) {
        throw new Error(`Route query "${queryName}" must define either \`queryFn\` or \`schema\`.`)
      }

      return [queryName, config] as const
    }),
  )
}

function assertCamelCase(str: string) {
  // Regular expression to validate camel case
  const camelCasePattern = /^[a-z][a-zA-Z0-9]*$/
  if (!camelCasePattern.test(str)) throw new InvalidIdentifierError(str)
}

export class InvalidIdentifierError extends Error {
  constructor(str: string) {
    super(`\`${str}\` must be camel cased`)
    this.name = 'InvalidIdentifierError'
  }
}
